import { useState, useCallback } from 'react';

/**
 * Custom hook for managing pagination state
 * Provides pagination logic without data fetching
 *
 * @param {number} initialLimit - Items per page (default: 20)
 * @returns {object} Pagination state and controls
 *
 * @example
 * const {
 *   pagination,
 *   setPagination,
 *   goToNextPage,
 *   goToPreviousPage,
 *   currentPage,
 *   totalPages,
 *   hasNextPage,
 *   hasPreviousPage
 * } = usePagination(50);
 */
export const usePagination = (initialLimit = 20) => {
  const [pagination, setPagination] = useState({
    limit: initialLimit,
    offset: 0,
    total: 0,
  });

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const hasNextPage = pagination.offset + pagination.limit < pagination.total;
  const hasPreviousPage = pagination.offset > 0;

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      setPagination(prev => ({
        ...prev,
        offset: prev.offset + prev.limit,
      }));
    }
  }, [hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      setPagination(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit),
      }));
    }
  }, [hasPreviousPage]);

  const goToPage = useCallback((pageNumber) => {
    const newOffset = (pageNumber - 1) * pagination.limit;
    if (newOffset >= 0 && newOffset < pagination.total) {
      setPagination(prev => ({
        ...prev,
        offset: newOffset,
      }));
    }
  }, [pagination.limit, pagination.total]);

  const resetPagination = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      offset: 0,
    }));
  }, []);

  return {
    pagination,
    setPagination,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    resetPagination,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
  };
};
